import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

import { MutableRefObject, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { t } from "i18next";
import { createFileXlsx } from "./createFileXlsx";
import { getFilteredDataGridRows } from "./getFilteredDataGridRows";
import {
  DownloadOutlined,
  LockOpenOutlined,
  LockOutlined,
} from "../../materialUi/materialUi";
import { ButtonDS } from "../Button";
import { Spacer } from "../Spacer";
import { colors } from "../../../styles/figmaColors";
import { dateFormatterDayMonthYearLong2Digits } from "../../../format";
import MaterialUiDatePicker from "../materialDatePicker/MaterialUiDatepicker";
import { MaterialUiSingleDatePicker } from "../materialDatePicker/MaterialUiSingleDatePicker";
import {
  MaterialUiMonthPicker,
  MonthPeriod,
} from "../materialDatePicker/MaterialUiMonthPicker";

type ActionProps = {
  columns: string[];
  onClick: (k: string) => void;
  className: string;
  pageName: string;
  columnFiltered: string;
};

function Action({
  columns,
  onClick,
  className,
  pageName,
  columnFiltered,
}: ActionProps) {
  return (
    <StyledFlex className={className}>
      <Spacer x={1} />
      <StyledColumn>
        {columns.map((col) => (
          <div key={col}>
            <Spacer y={1} />
            <ButtonDS
              text={t(`${pageName}.column.${col}`)}
              onClick={() => onClick(col)}
              buttonType={columnFiltered === col ? "primary" : "secondary"}
              format={"hug"}
            />
          </div>
        ))}
      </StyledColumn>
    </StyledFlex>
  );
}

const columnsToHideInView = ["type", "opendetails", ""];

const getDownloadFileBuffer = <
  Key extends string,
  T extends Record<Key, string | number>,
>(
  apiRef: MutableRefObject<GridApiPremium>,
  pageName: string,
) => {
  if (!apiRef.current) return;
  const columns = apiRef.current.getVisibleColumns() ?? [];

  const headerTitles = columns
    .filter(
      (column) =>
        !columnsToHideInView.includes(column.field.toLowerCase()) &&
        !column.field.toLowerCase().includes("bucketfilenamedownload"),
    )
    .map((column) => column.headerName ?? "");

  const headerKeys = columns
    .map((column) => column.field)
    .filter(
      (h) =>
        !columnsToHideInView.includes(h.toLowerCase()) &&
        !h.toLowerCase().includes("bucketfilenamedownload"),
    ) as (keyof T)[];

  const gridContent = getFilteredDataGridRows(apiRef) as T[];

  const bodyList = gridContent.map((l) => {
    return headerKeys.map((k) => {
      if (k === "stationsInfo") {
        return l[k].toString()?.replace("<br/>", " / ") ?? "";
      } else {
        return l[k] instanceof Date
          ? dateFormatterDayMonthYearLong2Digits(l[k])
          : (l[k] ?? "");
      }
    });
  });

  return createFileXlsx({
    tabTitle: t(`${pageName}.view.title`),
    headerTitles,
    body: bodyList,
  });
};

type ToolbarProps = {
  downloadViewAction?: (file: Buffer | undefined) => void;
  downloadDetailsAction?: () => void;
  pageName: string;
  apiRef: MutableRefObject<GridApiPremium>;
  dateFiltersFields?: string[];
  columnPinnedAction?: () => void;
  hasFirstColumnPinned?: boolean;
  dateRange?: {
    dateFiltersCallBack?: (dates: Date[]) => void;
    dateRangeCallBack?: [start: string, end: string] | undefined;
  };
  singleDate?: {
    singleDateFilterCallBack?: (date: Date) => void;
    singleDateCallBack?: Date | undefined;
    title?: string;
  };
  monthPicker?: {
    monthFilterCallBack: (dates: MonthPeriod) => void;
    monthFilterValue: MonthPeriod | undefined;
  };
};

export default function CustomToolbar(props: ToolbarProps) {
  const {
    downloadViewAction,
    downloadDetailsAction,
    dateFiltersFields,
    pageName,
    columnPinnedAction,
    hasFirstColumnPinned,
    dateRange,
    singleDate,
    monthPicker,
  } = props;
  const ref = props.apiRef.current;
  const [columnFiltered, setColumnFiltered] = useState<string | null>(
    dateFiltersFields ? dateFiltersFields[0] : null,
  );
  const [dateRangeValue, setDateRangeValue] = useState<Date[] | null>(null);
  const [dateRangeCallBackValue, setDateRangeCallBackValue] = useState<
    [Date, Date] | undefined
  >(
    dateRange?.dateRangeCallBack
      ? [
          new Date(dateRange?.dateRangeCallBack[0]),
          new Date(dateRange?.dateRangeCallBack[1]),
        ]
      : undefined,
  );

  const [dateSingleCallBackValue, setDateSingleCallBackValue] =
    useState<Date | null>(
      singleDate?.singleDateCallBack
        ? new Date(singleDate?.singleDateCallBack)
        : null,
    );

  const dateFilterValue: [start: Date, end: Date] | undefined = useMemo(() => {
    if (!ref?.state.filter) return undefined;

    const storedDateFilterValues = ref.state.filter.filterModel.items
      .filter((item) => dateFiltersFields?.includes(item.field))
      .map((item) => item.value as Date);

    return storedDateFilterValues.length === 2
      ? [storedDateFilterValues[0], storedDateFilterValues[1]]
      : undefined;
  }, [dateFiltersFields, ref.state.filter]);

  /** Compare timestamps (ms) so grid ↔ state stay stable when MUI recreates the same filter. */
  const toMilliSeconds = (v: unknown): number | null => {
    if (v instanceof Date) return v.getTime();
    if (typeof v === "string" || typeof v === "number") {
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? null : t;
    }
    return null;
  };

  // Sync DataGrid date filter → date picker state.
  // Without value guards, MUI often recreates the same items → setState → infinite loop.
  useEffect(() => {
    if (!ref?.state?.filter) return;
    const oldFilters = ref.state.filter.filterModel.items;
    if (oldFilters.length === 0) return;
    const startFilter = oldFilters.find((filter) => filter.id === "start");
    const endFilter = oldFilters.find((filter) => filter.id === "end");
    if (!startFilter || !endFilter) return;

    const field = startFilter.field;
    const ms0 = toMilliSeconds(startFilter.value);
    const ms1 = toMilliSeconds(endFilter.value);
    // Unreadable values: leave state unchanged.
    if (ms0 === null || ms1 === null) return;

    setColumnFiltered(field);
    setDateRangeValue((prev) => {
      // Same range as in state: keep `prev` (same reference) so the next effect does not re-fire.
      if (
        prev?.length === 2 &&
        toMilliSeconds(prev[0]) === ms0 &&
        toMilliSeconds(prev[1]) === ms1
      ) {
        return prev;
      }
      return [new Date(ms0), new Date(ms1)];
    });
  }, [ref?.state?.filter, ref?.state?.filter?.filterModel?.items]);

  // Sync date picker state → DataGrid filter.
  useEffect(() => {
    // Not ready yet or incomplete range.
    if (
      !ref ||
      !columnFiltered ||
      dateRangeValue?.length !== 2 ||
      !dateRangeValue[0] ||
      !dateRangeValue[1]
    ) {
      return;
    }

    const items = ref.state?.filter?.filterModel?.items ?? [];
    const start = items.find((i) => i.id === "start");
    const end = items.find((i) => i.id === "end");
    const want0 = toMilliSeconds(dateRangeValue[0]);
    const want1 = toMilliSeconds(dateRangeValue[1]);
    // Invalid picked dates.
    if (want0 === null || want1 === null) return;

    // Grid already has this filter: skip upsertFilterItems (otherwise loop).
    if (
      start &&
      end &&
      start.field === columnFiltered &&
      end.field === columnFiltered &&
      toMilliSeconds(start.value) === want0 &&
      toMilliSeconds(end.value) === want1
    ) {
      return;
    }

    ref.upsertFilterItems([
      {
        id: "start",
        field: columnFiltered,
        operator: "onOrAfter",
        value: dateRangeValue[0],
      },
      {
        id: "end",
        field: columnFiltered,
        operator: "onOrBefore",
        value: dateRangeValue[1],
      },
    ]);
    dateRange?.dateFiltersCallBack?.(dateRangeValue);
  }, [columnFiltered, dateRange, dateRangeValue, ref]);

  return (
    <>
      <StyledHeaderContainer>
        <Spacer x={17} />
        {downloadViewAction && (
          <>
            <Spacer x={1.5} />
            <ButtonDS
              format={"hug"}
              buttonType={"secondary"}
              onClick={() => {
                downloadViewAction &&
                  downloadViewAction(
                    getDownloadFileBuffer(props.apiRef, props.pageName),
                  );
              }}
              text={t("excel.downloadView")}
              leftIcon={<DownloadOutlined />}
              height="2.7"
            />
          </>
        )}
        {downloadDetailsAction && (
          <>
            <Spacer x={2.5} />
            <ButtonDS
              format={"hug"}
              buttonType={"secondary"}
              onClick={() => {
                downloadDetailsAction && downloadDetailsAction();
              }}
              text={t("excel.downloadDetails")}
              leftIcon={<DownloadOutlined />}
              height="2.7"
            />
          </>
        )}
        <Spacer x={2.5} />
        {columnPinnedAction && (
          <>
            <ButtonDS
              format={"hug"}
              buttonType={hasFirstColumnPinned ? "primary" : "secondary"}
              onClick={() => {
                columnPinnedAction && columnPinnedAction();
              }}
              text={t(`column.${hasFirstColumnPinned ? "unpinned" : "pinned"}`)}
              leftIcon={
                hasFirstColumnPinned ? <LockOpenOutlined /> : <LockOutlined />
              }
              height="2.7"
            />
            <Spacer x={2.5} />
          </>
        )}
        <GridToolbarColumnsButton
          slotProps={{
            button: {
              startIcon: "",
              //endIcon: <KeyboardArrowDownOutlined />,
              sx: {
                border: `0.0625rem solid ${colors["colors/borders/button/primary"]}`,
                height: "2.7rem",
                borderRadius: "0.5rem",
                minWidth: "6rem",
                boxShadow: "2px 1px 4px 0px rgba(122, 122, 122, 0.08)",
              },
            },
          }}
        />
        <Spacer x={2.5} />
        <GridToolbarFilterButton
          slotProps={{
            button: {
              //endIcon: <KeyboardArrowDownOutlined />,
              sx: {
                border: `0.0625rem solid ${colors["colors/borders/button/primary"]}`,
                height: "2.7rem",
                borderRadius: "0.5rem",
                minWidth: "6rem",
                boxShadow: "2px 1px 4px 0px rgba(122, 122, 122, 0.08)",
              },
            },
          }}
        />
        {dateFiltersFields?.length !== 0 ? (
          <>
            <Spacer x={2.5} />
            <MaterialUiDatePicker
              onChange={setDateRangeValue}
              value={dateFilterValue}
              Action={(props) => (
                <Action
                  {...props}
                  columns={dateFiltersFields}
                  onClick={(col: string) => setColumnFiltered(col)}
                  pageName={pageName}
                  columnFiltered={columnFiltered}
                />
              )}
            />
          </>
        ) : dateRange ? (
          <>
            <Spacer x={2.5} />
            <MaterialUiDatePicker
              onChange={(dates: Date[]) => {
                setDateRangeCallBackValue([dates[0], dates[1]]);
                dateRange.dateFiltersCallBack?.(dates);
              }}
              value={dateRangeCallBackValue}
            />
          </>
        ) : (
          singleDate && (
            <>
              <Spacer x={2.5} />
              <MaterialUiSingleDatePicker
                onChange={(date: Date) => {
                  setDateSingleCallBackValue(date);
                  singleDate.singleDateFilterCallBack?.(date);
                }}
                value={dateSingleCallBackValue}
                title={singleDate.title}
              />
            </>
          )
        )}
      </StyledHeaderContainer>
      {monthPicker && (
        <>
          <Spacer y={2} />
          <div>
            <MaterialUiMonthPicker
              onChange={monthPicker.monthFilterCallBack}
              value={monthPicker.monthFilterValue}
            />
          </div>
        </>
      )}
      <Spacer y={2} />
    </>
  );
}

const StyledHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  & svg {
    width: 1.25rem;
    height: 1.1rem;
  }
  white-space: nowrap;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
